import React from 'react';

import { useMutation } from '@apollo/react-hooks';
import { Form, Formik } from 'formik';
import useReactRouter from 'use-react-router';
import { handleFormError } from '../utils/gql-utils';

import SpinnerButton from '@do/walrus/SpinnerButton';
import TextInput from '@do/walrus/TextInput';
import copy from '../copy';
import { RESET_PASSWORD } from '../queries/queries';
import { ErrorBanner } from './Common';

import * as Yup from 'yup';
import { yupPasswordValidation } from '../utils/validation-utils';

const passwordResetValidation = Yup.object().shape({
  password: yupPasswordValidation
});

interface ResetPasswordGqlResponse {
  resetPassword: {
    status: string;
  };
}

interface ResetPasswordGqlVariables {
  resetPasswordRequest: {
    token: string;
    password: string;
  };
}

interface Props {
  token: string;
}

const PasswordResetForm: React.FC<Props> = ({ token }) => {
  const [resetPassword, { loading }] = useMutation<
    ResetPasswordGqlResponse,
    ResetPasswordGqlVariables
  >(RESET_PASSWORD);

  const [genericServerError, setGenericServerError] = React.useState(null);
  const { history } = useReactRouter();

  return (
    <Formik
      initialValues={{
        password: ''
      }}
      validationSchema={passwordResetValidation}
      onSubmit={(values, { setErrors }) => {
        setGenericServerError(null); /* Reset displayed error */
        resetPassword({
          variables: {
            resetPasswordRequest: { password: values.password, token }
          }
        })
          .then(() => {
            history.push({
              pathname: '/login',
              state: { success: 'reset' }
            });
          })
          .catch(handleFormError(setErrors, setGenericServerError));
      }}
    >
      {({ errors, values, touched }) => (
        <Form>
          {genericServerError && <ErrorBanner error={genericServerError} />}

          <TextInput
            type="password"
            placeholder={copy.form.password_reset.label}
            name="password"
            required
            label={copy.form.password_reset.placeholder}
            touched={touched.password}
            error={errors.password}
            value={values.password}
            autoFocus
          />
          <SpinnerButton
            className="do-u-mt--medium"
            type="submit"
            fullWidth
            spinning={loading}
            disabled={loading}
            loadingMessage={copy.form.password_reset.loading}
          >
            {copy.form.password_reset.button_text}
          </SpinnerButton>
        </Form>
      )}
    </Formik>
  );
};

export default PasswordResetForm;
